import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'

const API_BASE = import.meta.env.PROD
  ? 'https://derm-api.brian-mabry-edwards.workers.dev'
  : ''

interface Patient {
  id: string
  first_name: string
  last_name: string
  date_of_birth?: string
  notes?: string
  created_at: string
}

export default function Patients() {
  const [patients, setPatients] = useState<Patient[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')

  // Create patient modal state
  const [showCreate, setShowCreate] = useState(false)
  const [newFirstName, setNewFirstName] = useState('')
  const [newLastName, setNewLastName] = useState('')
  const [newDob, setNewDob] = useState('')
  const [newNotes, setNewNotes] = useState('')
  const [isCreating, setIsCreating] = useState(false)
  const [createSuccess, setCreateSuccess] = useState<string | null>(null)

  useEffect(() => {
    fetchPatients()
  }, [])

  const fetchPatients = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await fetch(`${API_BASE}/api/v1/patients`)
      if (!response.ok) {
        throw new Error(`Failed to fetch patients: ${response.status}`)
      }
      const data = await response.json()
      setPatients(data.patients || [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load patients')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCreatePatient = async () => {
    if (!newFirstName.trim() || !newLastName.trim()) {
      setError('First name and last name are required')
      return
    }

    setIsCreating(true)
    setError(null)

    try {
      const response = await fetch(`${API_BASE}/api/v1/patients`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: newFirstName.trim(),
          last_name: newLastName.trim(),
          date_of_birth: newDob || undefined,
          notes: newNotes.trim() || undefined,
        }),
      })

      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        throw new Error(data.error || `Failed to create patient: ${response.status}`)
      }

      const data = await response.json()
      const newPatient = data.patient as Patient

      setPatients((prev) => [newPatient, ...prev])
      setShowCreate(false)
      setNewFirstName('')
      setNewLastName('')
      setNewDob('')
      setNewNotes('')
      setCreateSuccess(`Patient "${newPatient.first_name} ${newPatient.last_name}" created successfully`)
      setTimeout(() => setCreateSuccess(null), 3000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create patient')
    } finally {
      setIsCreating(false)
    }
  }

  const filteredPatients = patients.filter((p) => {
    const searchLower = search.toLowerCase()
    return (
      p.first_name.toLowerCase().includes(searchLower) ||
      p.last_name.toLowerCase().includes(searchLower)
    )
  })

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Patients</h1>
          <p className="mt-1 text-gray-600">
            Manage patient records and view their image history
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
        >
          + New Patient
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border-2 border-red-500 rounded-lg p-4">
          <h3 className="text-red-800 font-bold text-lg">ERROR</h3>
          <pre className="text-red-700 text-sm mt-2 whitespace-pre-wrap font-mono">{error}</pre>
        </div>
      )}

      {createSuccess && (
        <div className="bg-green-50 border-2 border-green-500 rounded-lg p-4">
          <p className="text-green-800 font-medium">{createSuccess}</p>
        </div>
      )}

      {/* Create Patient Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h2 className="text-xl font-bold mb-4">Create New Patient</h2>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name *
                  </label>
                  <input
                    type="text"
                    value={newFirstName}
                    onChange={(e) => setNewFirstName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    value={newLastName}
                    onChange={(e) => setNewLastName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date of Birth
                </label>
                <input
                  type="date"
                  value={newDob}
                  onChange={(e) => setNewDob(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={newNotes}
                  onChange={(e) => setNewNotes(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Optional notes about the patient..."
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowCreate(false)
                  setNewFirstName('')
                  setNewLastName('')
                  setNewDob('')
                  setNewNotes('')
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreatePatient}
                disabled={isCreating || !newFirstName.trim() || !newLastName.trim()}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isCreating ? 'Creating...' : 'Create Patient'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search patients by name..."
          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        />
      </div>

      {/* Patient List */}
      <div className="bg-white rounded-lg shadow border border-gray-200">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Loading patients...</div>
        ) : filteredPatients.length === 0 ? (
          <div className="p-8 text-center">
            {patients.length === 0 ? (
              <div>
                <p className="text-gray-500 mb-4">No patients yet.</p>
                <button
                  onClick={() => setShowCreate(true)}
                  className="text-primary-600 hover:text-primary-800 font-medium"
                >
                  Create your first patient
                </button>
              </div>
            ) : (
              <p className="text-gray-500">No patients match your search.</p>
            )}
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredPatients.map((patient) => (
              <Link
                key={patient.id}
                to={`/patients/${patient.id}`}
                className="block p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-medium text-gray-900">
                      {patient.first_name} {patient.last_name}
                    </h3>
                    {patient.date_of_birth && (
                      <p className="text-sm text-gray-500">DOB: {patient.date_of_birth}</p>
                    )}
                    {patient.notes && (
                      <p className="text-sm text-gray-500 mt-1 line-clamp-1">{patient.notes}</p>
                    )}
                  </div>
                  <span className="text-gray-400">→</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="text-center text-sm text-gray-500">
        {patients.length} patient{patients.length !== 1 ? 's' : ''} total
        {search && filteredPatients.length !== patients.length && (
          <span> • {filteredPatients.length} matching search</span>
        )}
      </div>
    </div>
  )
}
